import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import AuthVisual from '@/components/AuthVisual';
import '@/styles/auth.css';

/* ─── Helpers ─── */
function formatError(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || 'Une erreur est survenue';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join('. ');
  return String(d);
}
const reEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function scorePassword(p) {
  if (!p) return 0;
  let s = 0;
  if (p.length >= 8) s++;
  if (/[A-Z]/.test(p)) s++;
  if (/[0-9]/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p) || p.length >= 12) s++;
  return s;
}
const PWD_LABELS = ['', 'Faible', 'Moyen', 'Bon', 'Excellent'];
const PWD_CLASS  = ['', 'w', 'm', 'm', 's'];

function Toast({ message, type, show }) {
  return (
    <div
      className={`c2s-toast ${show ? 'on' : ''} ${type === 'success' ? 'toast--success' : ''}`}
      role="status" aria-live="polite"
    >
      {message}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────── */

export default function RegisterPage() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get('role'); // 'sender' | 'carrier' | null

  const [step, setStep] = useState(1);
  const [profileType, setProfileType] = useState(null); // 'part' | 'pro'
  const [roles, setRoles] = useState(() => {
    if (initialRole === 'sender')  return ['exp'];
    if (initialRole === 'carrier') return ['tra'];
    return [];
  });

  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName]   = useState('');
  const [company_name, setCompanyName] = useState('');
  const [siret, setSiret]   = useState('');
  const [sector, setSector] = useState('');
  const [phone, setPhone]   = useState('');
  const [email, setEmail]   = useState('');
  const [password, setPassword]     = useState('');
  const [password2, setPassword2]   = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [consent, setConsent]   = useState(false);

  // Erreurs
  const [errs, setErrs] = useState({});
  const [loading, setLoading] = useState(false);

  const [toast, setToast] = useState({ message: '', type: 'default', show: false });
  const toastTimer = useRef(null);
  const showToast = (message, type = 'default') => {
    setToast({ message, type, show: true });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(t => ({ ...t, show: false })), 2800);
  };

  useEffect(() => {
    // Compte neuf : direction « Mes vérifications », où saisir le code reçu par e-mail
    if (user) navigate(user.roles?.includes('ROLE_ADMIN') ? '/admin/dashboard'
      : user.verifications?.types?.email === 'VERIFIED' ? '/app/dashboard' : '/app/verifications');
  }, [user, navigate]);

  /* ─── Toggle profile type ─── */
  const togProfileType = (t) => {
    setProfileType(t);
    setErrs(e => ({ ...e, profileType: false }));
  };

  /* ─── Toggle role ─── */
  const togRole = (r) => {
    setRoles(rs => rs.includes(r) ? rs.filter(x => x !== r) : [...rs, r]);
    setErrs(e => ({ ...e, roles: false }));
  };

  /* ─── Stepper ─── */
  const toStep = (n) => {
    if (n === 2) {
      const newErrs = {};
      if (!profileType) newErrs.profileType = true;
      if (roles.length === 0) newErrs.roles = true;
      if (Object.keys(newErrs).length) {
        setErrs(newErrs);
        showToast('Veuillez compléter votre choix');
        return;
      }
    }
    if (n === 3) {
      const ok = validateStep2();
      if (!ok) return;
      handleSubmit();
      return;
    }
    setStep(n);
    setErrs({});
  };

  /* ─── Validation step 2 ─── */
  const validateStep2 = () => {
    const e = {};
    if (!first_name.trim()) e.first_name = true;
    if (!last_name.trim()) e.last_name = true;
    if (profileType === 'pro') {
      if (!company_name.trim()) e.company_name = true;
      if (!siret.trim()) e.siret = true;
      if (!phone.trim()) e.phone = true;
    }
    if (!reEmail.test(email.trim())) e.email = true;
    if (password.length < 8) e.password = true;
    if (password !== password2 || !password2) e.password2 = true;
    if (!consent) e.consent = true;
    setErrs(e);
    if (Object.keys(e).length) {
      showToast('Veuillez corriger les erreurs du formulaire');
      return false;
    }
    return true;
  };

  /* ─── Submit ─── */
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        email: email.trim().toLowerCase(),
        password,
        first_name: first_name.trim(),
        last_name: last_name.trim(),
        roles: roles.map(r => r === 'exp' ? 'ROLE_SENDER' : 'ROLE_CARRIER'),
        profile_type: profileType === 'pro' ? 'PROFESSIONAL' : 'PARTICULAR',
        consent: true,
      };
      if (profileType === 'pro') {
        payload.company_name = company_name.trim();
        payload.siret = siret.trim();
        payload.sector = sector || null;
        payload.phone = phone.trim();
      } else if (phone.trim()) {
        payload.phone = phone.trim();
      }
      await register(payload);
      setStep(3);
      showToast('Compte créé avec succès ✓', 'success');
    } catch (err) {
      const msg = formatError(err);
      showToast(msg);
      if (msg.toLowerCase().includes('email')) setErrs(e => ({ ...e, email: true }));
    } finally {
      setLoading(false);
    }
  };

  /* ─── Step bar ─── */
  const StepBar = () => (
    <div className="steps-bar">
      <div className={`step-dot ${step === 1 ? 'step-dot--on' : 'step-dot--done'}`}>
        {step > 1 ? '✓' : '1'}
      </div>
      <div className={`step-line ${step >= 2 ? 'step-line--done' : ''}`} />
      <div className={`step-dot ${step === 2 ? 'step-dot--on' : step > 2 ? 'step-dot--done' : ''}`}>
        {step > 2 ? '✓' : '2'}
      </div>
      <div className={`step-line ${step >= 3 ? 'step-line--done' : ''}`} />
      <div className={`step-dot ${step === 3 ? 'step-dot--on' : ''}`}>3</div>
    </div>
  );

  const pwdScore = scorePassword(password);
  const profHint = (() => {
    if (roles.length === 2) return '✓ Mode hybride — vous pourrez basculer entre les rôles';
    if (roles.includes('exp')) return '✓ Vous pourrez envoyer des colis';
    if (roles.includes('tra')) return '✓ Vous pourrez transporter des colis et gagner de l\'argent';
    return '';
  })();

  return (
    <div className="auth-layout" data-testid="register-page">
      {/* ══ GAUCHE — Formulaire ══ */}
      <div className="form-side">
        <div className="form-wrap">
          {step === 1 && (
            <Link to="/login" className="back-link" data-testid="back-login">← Retour à la connexion</Link>
          )}

          <div className="brand">
            <div className="brand-mark">C2</div>
            <span className="brand-name">Colis<em>2</em>Ship</span>
          </div>

          <div className="form-hd">
            <h1 className="form-title">Créer mon compte</h1>
            <p className="form-sub">Envoyez ou transportez des colis entre l'Europe, l'Afrique et l'Amérique</p>
          </div>

          <StepBar />

          {/* ── ÉTAPE 1 : Type compte + rôles ── */}
          {step === 1 && (
            <>
              {/* Type compte exclusif */}
              <p className="step-label">Type de compte</p>
              <div className="prof-grid">
                <div
                  className={`pc ${profileType === 'part' ? 'sel' : ''}`}
                  onClick={() => togProfileType('part')}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && togProfileType('part')}
                  role="radio" aria-checked={profileType === 'part'} tabIndex={0}
                  data-testid="profile-particulier"
                >
                  <div className="pc-chk">✓</div>
                  <div className="pc-ico">👤</div>
                  <div className="pc-name">Particulier</div>
                  <div className="pc-desc">J'utilise Colis2Ship à titre personnel</div>
                  <div className="pc-badge pc-badge--exp">Personnel</div>
                </div>
                <div
                  className={`pc ${profileType === 'pro' ? 'sel' : ''}`}
                  onClick={() => togProfileType('pro')}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && togProfileType('pro')}
                  role="radio" aria-checked={profileType === 'pro'} tabIndex={0}
                  data-testid="profile-pro"
                >
                  <div className="pc-chk">✓</div>
                  <div className="pc-ico">🏢</div>
                  <div className="pc-name">Entreprise</div>
                  <div className="pc-desc">J'expédie ou je transporte pour le compte d'une société (SIREN)</div>
                  <div className="pc-badge pc-badge--tra">Entreprise</div>
                </div>
              </div>
              {errs.profileType && <div className="ferr on">Veuillez choisir un type de compte</div>}

              <div className="step-sep" />

              {/* Rôle cumulable */}
              <p className="step-label">Votre rôle (vous pouvez sélectionner les deux)</p>
              <div className="prof-grid">
                <div
                  className={`pc ${roles.includes('exp') ? 'sel' : ''}`}
                  onClick={() => togRole('exp')}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && togRole('exp')}
                  role="checkbox" aria-checked={roles.includes('exp')} tabIndex={0}
                  data-testid="role-sender"
                >
                  <div className="pc-chk">✓</div>
                  <div className="pc-ico">📦</div>
                  <div className="pc-name">Expéditeur</div>
                  <div className="pc-desc">J'envoie des colis vers l'Afrique et partout dans le monde</div>
                  <div className="pc-badge pc-badge--exp">Envoyer</div>
                </div>
                <div
                  className={`pc ${roles.includes('tra') ? 'sel' : ''}`}
                  onClick={() => togRole('tra')}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && togRole('tra')}
                  role="checkbox" aria-checked={roles.includes('tra')} tabIndex={0}
                  data-testid="role-carrier"
                >
                  <div className="pc-chk">✓</div>
                  <div className="pc-ico">🚗</div>
                  <div className="pc-name">Transporteur</div>
                  <div className="pc-desc">Je voyage régulièrement et transporte des colis pour gagner de l'argent</div>
                  <div className="pc-badge pc-badge--tra">Transporter</div>
                </div>
              </div>
              <div className="prof-selected-hint">{profHint}</div>
              {errs.roles && <div className="ferr on">Veuillez sélectionner au moins un rôle</div>}

              <button
                type="button"
                className="cta cta--primary"
                onClick={() => toStep(2)}
                data-testid="btn-step1-continue"
              >
                Continuer →
              </button>
            </>
          )}

          {/* ── ÉTAPE 2 : Infos ── */}
          {step === 2 && (
            <>
              <div className="two-col">
                <div className="fg">
                  <label className="flbl" htmlFor="rPren">Prénom</label>
                  <input
                    type="text" id="rPren" className={`fi ${errs.first_name ? 'err' : ''}`}
                    placeholder="Jean" autoComplete="given-name"
                    value={first_name}
                    onChange={e => { setFirstName(e.target.value); setErrs(x => ({ ...x, first_name: false })); }}
                    data-testid="input-first-name"
                  />
                  {errs.first_name && <div className="ferr on">Champ requis</div>}
                </div>
                <div className="fg">
                  <label className="flbl" htmlFor="rNom">Nom</label>
                  <input
                    type="text" id="rNom" className={`fi ${errs.last_name ? 'err' : ''}`}
                    placeholder="Dupont" autoComplete="family-name"
                    value={last_name}
                    onChange={e => { setLastName(e.target.value); setErrs(x => ({ ...x, last_name: false })); }}
                    data-testid="input-last-name"
                  />
                  {errs.last_name && <div className="ferr on">Champ requis</div>}
                </div>
              </div>

              {/* Bloc Pro conditionnel */}
              {profileType === 'pro' && (
                <div className="pro-block">
                  <div className="pro-block-hd">🏢 Informations sur l'entreprise</div>

                  <div className="fg">
                    <label className="flbl" htmlFor="rCompany">Raison sociale</label>
                    <input
                      type="text" id="rCompany" className={`fi ${errs.company_name ? 'err' : ''}`}
                      placeholder="Ma Société SARL" autoComplete="organization"
                      value={company_name}
                      onChange={e => { setCompanyName(e.target.value); setErrs(x => ({ ...x, company_name: false })); }}
                      data-testid="input-company"
                    />
                    {errs.company_name && <div className="ferr on">Champ requis</div>}
                  </div>

                  <div className="two-col">
                    <div className="fg">
                      <label className="flbl" htmlFor="rSiret">SIRET / N° pro</label>
                      <input
                        type="text" id="rSiret" className={`fi ${errs.siret ? 'err' : ''}`}
                        placeholder="123 456 789 00012"
                        value={siret}
                        onChange={e => { setSiret(e.target.value); setErrs(x => ({ ...x, siret: false })); }}
                        data-testid="input-siret"
                      />
                      {errs.siret && <div className="ferr on">Champ requis</div>}
                    </div>
                    <div className="fg">
                      <label className="flbl" htmlFor="rSector">Secteur d'activité</label>
                      <select
                        id="rSector" className="fi" style={{ paddingRight: 14 }}
                        value={sector} onChange={e => setSector(e.target.value)}
                        data-testid="input-sector"
                      >
                        <option value="">Choisir…</option>
                        <option>Logistique / Transport</option>
                        <option>Commerce / Négoce</option>
                        <option>Import / Export</option>
                        <option>E-commerce</option>
                        <option>Autre</option>
                      </select>
                    </div>
                  </div>

                  <div className="fg">
                    <label className="flbl" htmlFor="rPhone">Téléphone professionnel</label>
                    <div className="fi-wrap">
                      <input
                        type="tel" id="rPhone" className={`fi ${errs.phone ? 'err' : ''}`}
                        placeholder="+33 6 00 00 00 00" autoComplete="tel"
                        value={phone}
                        onChange={e => { setPhone(e.target.value); setErrs(x => ({ ...x, phone: false })); }}
                        data-testid="input-phone"
                      />
                      <span className="fi-ico">📞</span>
                    </div>
                    {errs.phone && <div className="ferr on">Champ requis</div>}
                  </div>
                </div>
              )}

              {/* Email */}
              <div className="fg">
                <label className="flbl" htmlFor="rEmail">Email</label>
                <div className="fi-wrap">
                  <input
                    type="email" id="rEmail"
                    className={`fi ${errs.email ? 'err' : ''} ${email && reEmail.test(email) ? 'ok' : ''}`}
                    placeholder="jean@exemple.com" autoComplete="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setErrs(x => ({ ...x, email: false })); }}
                    data-testid="input-email"
                  />
                  <span className="fi-ico">✉</span>
                </div>
                {errs.email && <div className="ferr on">Adresse email invalide</div>}
              </div>

              {/* Password */}
              <div className="fg">
                <label className="flbl" htmlFor="rPwd">Mot de passe</label>
                <div className="fi-wrap">
                  <input
                    type={showPwd ? 'text' : 'password'} id="rPwd"
                    className={`fi ${errs.password ? 'err' : ''}`}
                    placeholder="Minimum 8 caractères" autoComplete="new-password"
                    value={password}
                    onChange={e => { setPassword(e.target.value); setErrs(x => ({ ...x, password: false })); }}
                    data-testid="input-password"
                  />
                  <button
                    type="button" className="fi-ico fi-ico--eye"
                    onClick={() => setShowPwd(s => !s)}
                    aria-label={showPwd ? 'Masquer' : 'Afficher'}
                    data-testid="btn-toggle-password"
                  >
                    {showPwd ? '🙈' : '👁'}
                  </button>
                </div>
                <div className="pwd-bars">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className={`pwd-b ${i <= pwdScore ? PWD_CLASS[pwdScore] : ''}`} />
                  ))}
                </div>
                <div className={`pwd-lbl ${PWD_CLASS[pwdScore]}`}>{PWD_LABELS[pwdScore]}</div>
                {errs.password && <div className="ferr on">Minimum 8 caractères</div>}
              </div>

              {/* Confirm Password */}
              <div className="fg">
                <label className="flbl" htmlFor="rPwd2">Confirmer le mot de passe</label>
                <div className="fi-wrap">
                  <input
                    type={showPwd2 ? 'text' : 'password'} id="rPwd2"
                    className={`fi ${errs.password2 ? 'err' : (password2 && password === password2 ? 'ok' : '')}`}
                    placeholder="••••••••" autoComplete="new-password"
                    value={password2}
                    onChange={e => { setPassword2(e.target.value); setErrs(x => ({ ...x, password2: false })); }}
                    data-testid="input-password2"
                  />
                  <button
                    type="button" className="fi-ico fi-ico--eye"
                    onClick={() => setShowPwd2(s => !s)}
                    aria-label={showPwd2 ? 'Masquer' : 'Afficher'}
                  >
                    {showPwd2 ? '🙈' : '👁'}
                  </button>
                </div>
                {errs.password2 && <div className="ferr on">Les mots de passe ne correspondent pas</div>}
              </div>

              <label className="cgu-row" htmlFor="cguChk">
                <input
                  type="checkbox" id="cguChk" style={{ accentColor: 'var(--c2s-glt)' }}
                  checked={consent}
                  onChange={e => { setConsent(e.target.checked); setErrs(x => ({ ...x, consent: false })); }}
                  data-testid="checkbox-cgu"
                />
                <span>
                  J'accepte les{' '}
                  <a href="/conditions" target="_blank" rel="noopener noreferrer" data-testid="link-cgu">conditions générales</a>
                  {' '}et la{' '}
                  <a href="#privacy" onClick={(e) => { e.preventDefault(); showToast('Politique disponible bientôt'); }}>politique de confidentialité</a>
                </span>
              </label>
              {errs.consent && <div className="ferr on">Vous devez accepter les CGU</div>}

              <div className="step-nav">
                <button type="button" className="cta cta--ghost" onClick={() => { setStep(1); setErrs({}); }} data-testid="btn-step2-back">
                  ← Retour
                </button>
                <button
                  type="button" className="cta cta--primary"
                  onClick={() => toStep(3)} disabled={loading}
                  data-testid="btn-step2-submit"
                >
                  {loading && <span className="cta-ldr" />}
                  <span>{loading ? 'Création…' : 'Créer mon compte →'}</span>
                </button>
              </div>
            </>
          )}

          {/* ── ÉTAPE 3 : Succès ── */}
          {step === 3 && (
            <div style={{ textAlign: 'center' }}>
              <div className="succ-ico">✓</div>
              <p className="succ-title">Compte créé avec succès !</p>
              <p className="succ-sub">
                Code de vérification envoyé à <strong data-testid="confirm-email">{email}</strong>
              </p>

              {/* Prochaine étape réelle : vérifier l'adresse (T13 — une carte de double authentification annonçait ici une protection inexistante) */}
              <div className="next-card">
                <div className="next-card-hd">
                  <span className="next-card-ico">✉️</span>
                  <div>
                    <p className="next-card-title">Vérifiez votre adresse e-mail</p>
                    <p className="next-card-sub">Saisissez le code à 6 chiffres reçu : il vous faudra pour publier, proposer ou contacter un membre</p>
                  </div>
                </div>
                <button
                  type="button" className="cta cta--gold"
                  onClick={() => navigate('/app/verifications')}
                  data-testid="btn-go-verifications"
                >
                  Saisir mon code
                </button>
                <button
                  type="button" className="cta cta--ghost" style={{ fontSize: 12 }}
                  onClick={() => navigate('/app/dashboard')}
                  data-testid="btn-go-dashboard"
                >
                  Accéder à mon espace →
                </button>
              </div>
            </div>
          )}

          {step < 3 && (
            <p className="sw">
              Déjà un compte ? <Link to="/login" data-testid="link-login">Se connecter</Link>
            </p>
          )}
        </div>
      </div>

      {/* ══ DROITE — Visuel ══ */}
      <AuthVisual variant="register" />

      <Toast message={toast.message} type={toast.type} show={toast.show} />
    </div>
  );
}
